// Session & authentication-state model (2026-10-06).
//
// One pure decision function — deriveSessionStatus — decides what the signed-in
// browser session may render. A valid Firebase Auth session is NEVER proof of
// firm membership: the application shell renders only in ACTIVE, which requires
//   1. an authenticated Firebase user;
//   2. an existing users/{uid} profile;
//   3. an active membership status;
//   4. a non-empty string firmId;
//   5. an existing firms/{firmId} document;
//   6. that firm operational (status absent or exactly 'active').
// This mirrors the server (tenantIdentityService.resolveActiveFirmMember) and the
// rules (callerIsActiveMember / isOperationalFirm). The client decision is UX
// only; rules and callables stay authoritative.
//
// Cache vs server: Firestore snapshots carry metadata.fromCache. A snapshot is
// "confirmed" once the server has answered since the listener was (re)attached.
// The session only TRUSTS a snapshot when it is confirmed, or when this tab has
// already established ACTIVE for the same uid:firmId and is not revalidating.
// So a session that never reached ACTIVE cannot render firm data from the local
// cache just because the network is down, and a cached snapshot can never be
// mistaken for a definitive removal / suspension / firm loss on a cold start.

import { sessionEpochStatus, hasSessionEpoch, EPOCH_STATUS } from './sessionEpoch';

export const SESSION = Object.freeze({
  AUTH_LOADING: 'AUTH_LOADING',
  SIGNED_OUT: 'SIGNED_OUT',
  PROFILE_LOADING: 'PROFILE_LOADING',
  FIRM_LOADING: 'FIRM_LOADING',
  ACTIVE: 'ACTIVE',
  NO_FIRM: 'NO_FIRM',
  SUSPENDED: 'SUSPENDED',
  FIRM_UNAVAILABLE: 'FIRM_UNAVAILABLE',
  ERROR_RETRYABLE: 'ERROR_RETRYABLE',
});

// Definitive "access is not available" states (as opposed to loading / retry).
export const ACCESS_DENIED_STATES = Object.freeze([SESSION.NO_FIRM, SESSION.SUSPENDED, SESSION.FIRM_UNAVAILABLE]);

// Canonical client mirror of the server/rules firm lifecycle predicate:
// operational only when the firm document exists with status absent or exactly
// 'active'. Archived / deleted / null / '' / unknown statuses fail closed.
export const isFirmOperational = (firm) => !!firm && typeof firm === 'object' && (!('status' in firm) || firm.status === 'active');

// Same normalisation as resolveActiveFirmMember and the rules' callerIsActiveMember:
// absent / null / '' mean 'active'; anything else is the literal status.
export const normalizeMemberStatus = (profile) => {
  const s = profile ? profile.status : undefined;
  return s == null || s === '' ? 'active' : String(s);
};

export const tenantKeyOf = (uid, firmId) => (uid && firmId ? `${uid}:${firmId}` : null);

export const profileFirmId = (profile) => (profile && typeof profile.firmId === 'string' && profile.firmId !== '' ? profile.firmId : null);

// A listener refused by the rules (as opposed to a transport / server failure).
export const isPermissionDenied = (code) => /permission-denied|PERMISSION_DENIED|unauthenticated/i.test(String(code || ''));

// Slot shape (profile / firm):
//   { phase: 'pending'|'ok'|'missing'|'error'|'denied', data, fromCache, confirmed, firmId? }
// state shape: { auth, profile, firm, registering, stalled, revalidating, everActiveTenant, suspectStorm }
//   auth: 'loading' | null | { uid, ... }
export function deriveSessionStatus(state) {
  const { auth } = state;
  if (auth === 'loading' || auth === undefined) return { status: SESSION.AUTH_LOADING };
  if (!auth) return { status: SESSION.SIGNED_OUT };
  if (state.suspectStorm) return { status: SESSION.ERROR_RETRYABLE, reason: 'ACCESS_ERRORS' };

  const loading = (status, reason) => (state.stalled && !state.registering
    ? { status: SESSION.ERROR_RETRYABLE, reason: 'STALLED' }
    : { status, reason, registering: !!state.registering });

  const p = state.profile || { phase: 'pending' };
  // Incident 2026-11-03: while a sign-out is in progress (null auth event held
  // for the grace period) Firestore re-attaches users/{uid} without credentials
  // and the rules refuse it. That refusal is the sign-out itself, not a
  // connectivity failure — never show "couldn't reach the server" for it.
  if (p.phase === 'error' && state.authNullPending && isPermissionDenied(p.code)) return { status: SESSION.PROFILE_LOADING, reason: 'SIGNING_OUT' };
  if (p.phase === 'error') return { status: SESSION.ERROR_RETRYABLE, reason: 'PROFILE_READ_FAILED' };
  if (p.phase === 'pending') return loading(SESSION.PROFILE_LOADING);
  if (p.phase === 'missing') {
    if (!p.confirmed) return loading(SESSION.PROFILE_LOADING);
    // Registration-in-progress: the profile is created by bootstrapNewFirm AFTER
    // Firebase Auth already reports the user. Missing is expected; wait.
    if (state.registering) return { status: SESSION.PROFILE_LOADING, reason: 'REGISTERING', registering: true };
    // Incident 2026-10-21: a sign-out is in progress (null auth event held for the
    // grace period) — e.g. Login signing a new, unverified invitee out to verify
    // their email. Not a stranded account: wait for the sign-out to land.
    if (state.authNullPending) return { status: SESSION.PROFILE_LOADING, reason: 'SIGNING_OUT' };
    // P0-4: a confirmed authenticated user with no profile at all.
    return { status: SESSION.SIGNED_OUT, reason: 'PROFILE_MISSING' };
  }

  const profile = p.data || {};
  const firmId = profileFirmId(profile);
  const memberStatus = normalizeMemberStatus(profile);
  const tenant = tenantKeyOf(auth.uid, firmId);
  const trusted = (slot) => !!slot.confirmed || (!!tenant && state.everActiveTenant === tenant && !state.revalidating);
  if (!trusted(p)) return loading(SESSION.PROFILE_LOADING);

  // Token & Session Revocation Policy — Phase B: the session's sign-in time
  // (ID-token auth_time) must be at or after the profile's server-owned
  // security epoch. Checked before membership: a security-invalid session ends
  // whatever the firm state. Until auth_time is known (or if reading it failed)
  // a profile WITH an epoch keeps the session loading — a transient failure
  // stalls to ERROR_RETRYABLE, never to a false sign-out.
  if (hasSessionEpoch(profile)) {
    const at = state.authTime || { phase: 'pending' };
    if (at.phase !== 'ok') return loading(SESSION.PROFILE_LOADING);
    if (sessionEpochStatus(profile, at.value) !== EPOCH_STATUS.VALID) return { status: SESSION.SIGNED_OUT, reason: 'SESSION_ENDED' };
  }

  if (memberStatus === 'suspended') return { status: SESSION.SUSPENDED, reason: 'SUSPENDED' };
  if (memberStatus === 'removed') return { status: SESSION.NO_FIRM, reason: 'REMOVED' };
  // Any other non-active status is inactive server-side (INACTIVE); fail closed.
  if (memberStatus !== 'active') return { status: SESSION.SUSPENDED, reason: 'INACTIVE' };
  if (!firmId) return { status: SESSION.NO_FIRM, reason: 'NO_FIRM' };

  const f = state.firm || { phase: 'pending' };
  if (f.firmId !== firmId || f.phase === 'pending') return loading(SESSION.FIRM_LOADING);
  if (f.phase === 'denied') return loading(SESSION.FIRM_LOADING, 'REVALIDATING');
  if (f.phase === 'error') return { status: SESSION.ERROR_RETRYABLE, reason: 'FIRM_READ_FAILED' };
  if (!trusted(f)) return loading(SESSION.FIRM_LOADING);
  if (f.phase === 'missing') return { status: SESSION.FIRM_UNAVAILABLE, reason: 'FIRM_MISSING' };
  if (!isFirmOperational(f.data)) return { status: SESSION.FIRM_UNAVAILABLE, reason: 'FIRM_NOT_OPERATIONAL' };
  return { status: SESSION.ACTIVE, tenant };
}
